/**
 * Creature render manager — owns the per-creature Object3D map; reconciles
 * additions/removals from the schema's creatures map; updates positions and
 * animation state each frame.
 *
 * Mirrors the player-render pattern in main.ts (cubes map → creatures map).
 * Listens for "creature-death" broadcasts to play the death animation before
 * the schema removes the entry (otherwise the mesh just vanishes).
 */

import * as THREE from "three";
import type { Creature, CreatureType } from "@tribez/shared";
import type { AssetBundle } from "../assets";
import { createCreatureMesh, type CreatureMesh, type CreatureAction } from "./creature-mesh";
import { SnapshotBuffer } from "../net/snapshot-interp";
import { createChannelOrb } from "../abilities/cast-vfx-shapes";
import { addXraySilhouette, type XraySilhouette } from "../xray-silhouette";
import { tintFor } from "../player";

// Creature types that have no glTF mesh — rendered as a procedural VFX orb instead.
const ORB_RENDER_TYPES = new Set<string>(["Nightwisp"]);
const NIGHTWISP_ORB_COLOR = 0x9b6cff;

// Creatures render through the SAME buffered snapshot interpolation as players
// (net/snapshot-interp.ts, sampled INTERP_DELAY_MS behind now). The previous
// per-frame POS_LERP=0.25 chase of the latest schema position was the era-1
// smoothing the interp system replaced — stepped at 30Hz-data-vs-60fps-paint and
// framerate-dependent (not dt-scaled). Pets walk beside the interpolated hero,
// so the mismatch was side-by-side visible (2026-07-10 feel diagnosis, D3).
// A leash/respawn jump beyond this resets the buffer and snaps — a lerp would
// streak the mesh across the map.
const TELEPORT_DIST = 8; // u

// Forgiving click radius for the proximity fallback in pickCreature. Small
// creatures (coyotes, night wolves) present a tiny mesh that's frustrating to
// hit dead-on; if a click misses every mesh, we accept the nearest creature
// whose body axis passes within this many world-units of the ray. Tuned so the
// grab area roughly matches a mid-size creature footprint without stealing
// clearly-intended ground clicks farther away (playtest 2026-06-22).
const CREATURE_PICK_RADIUS = 1.2;
const _pickPt = new THREE.Vector3();

/** Local-player identity, for x-ray silhouette relation (own/ally/enemy) on pets. */
export interface CreatureRenderMe {
  sessionId: string;
  teamId: number;
}

export interface CreatureRenderManager {
  /**
   * Sync the creatures map from a Colyseus state snapshot. `me` drives the
   * x-ray silhouette tint for newly-spawned PETS (creature.ownerId !== "");
   * wild creatures never get a silhouette (see spawnMesh). Pass null before
   * the local cube exists yet — a pet spawned in that narrow window falls
   * back to the enemy tint, same conservative default the player-tint system
   * uses (main.ts cubes.onAdd). Optional/omittable for callers that predate
   * the x-ray feature — treated the same as null.
   */
  reconcile(creatures: Iterable<[string, Creature]>, me?: CreatureRenderMe | null): void;
  /**
   * Record an authoritative position into the creature's snapshot-interp
   * buffer. Call at PATCH DECODE ($(creature).onChange in main.ts) with
   * performance.now(), same contract as the player pipeline — recording at
   * frame cadence would quantize snapshot timestamps to frame boundaries.
   */
  record(id: string, x: number, z: number, now: number): void;
  /**
   * Per-frame update: position + facing + mixer. `renderT` is the interp
   * sample time (now − INTERP_DELAY_MS, same clock the player renderer uses);
   * without it (or before any snapshot) positions snap to the latest raw value.
   */
  update(dtSec: number, renderT?: number): void;
  /** Raycast against alive creatures; returns the creature id or null. */
  pickCreature(raycaster: THREE.Raycaster): string | null;
  /** Return the root Object3D for a creature by id, or null if not spawned. */
  getObject(id: string): THREE.Object3D | null;
  /** QA-only: per-creature mesh/material/animation facts (read-only). */
  inspect(): Array<{
    id: string;
    meshCount: number;
    materialTypes: string[];
    hasTextureMap: boolean;
    pureWhite: boolean;
    mixerRunning: boolean;
    activeActions: number;
    boneSample: number[] | null;
    /** RENDERED world position (interp output) — lets a probe assert the
     *  renderer tracks the authoritative position (D3 snapshot-interp port). */
    renderPos: [number, number];
  }>;
  /** Dispose all meshes. */
  dispose(): void;
}

export function createCreatureRender(
  scene: THREE.Scene,
  assets: AssetBundle,
  sampleHeight: (x: number, z: number) => number,
): CreatureRenderManager {
  const meshes = new Map<string, {
    mesh: CreatureMesh;
    lastAiState: string;
    // Latest raw server position — the no-snapshot fallback (spawn frame,
    // callers that never wire record()).
    targetX: number;
    targetZ: number;
    // Pets only (see spawnMesh) — null for wild creatures and the orb-backed path.
    xray: XraySilhouette | null;
  }>();
  // Snapshot-interp buffers, keyed like `meshes`. Kept separate: record() can
  // arrive (creatures.onAdd) before the mesh spawns, and a missing-mesh type
  // still buffers harmlessly. `lastRec` backs the teleport guard — the buffer
  // itself only sees teleports across a freeze gap; a mid-motion leash jump
  // needs an explicit reset or the lerp streaks the mesh across the map.
  const bufs = new Map<string, SnapshotBuffer>();
  const lastRec = new Map<string, { x: number; z: number }>();
  // Orb-tick functions for procedural-VFX creatures (Nightwisp, etc.).
  // Stored separately to avoid polluting the CreatureMesh type.
  const orbTicks = new Map<string, (dtMs: number) => void>();
  // Dedupe "no mesh for type=X" warnings per type — reconcile runs at frame
  // cadence and would otherwise spam the console thousands of times for a
  // single creature with a missing-or-undefined type. Two cases hit this:
  //   1. @colyseus/schema's decoder fail-soft path ("refId not found";
  //      `index.js:4564`) leaves a Cube/Creature half-decoded → `creatureType`
  //      reads as `undefined` on the client and never recovers.
  //   2. A legitimate type exists on the server but its mesh isn't preloaded
  //      yet (e.g. undead types before their FBX lands).
  // Both are persistent states, so one warning per offending type is enough.
  const warnedMissingTypes = new Set<string>();

  // Relation tint for a pet's x-ray silhouette — reuses player.ts's tintFor
  // ladder directly (own/ally/enemy/dummy) instead of a second hand-rolled
  // copy. Wild creatures (ownerId === "") never reach this — see the `isPet`
  // gate at each call site below. Falls back to the enemy bucket when `me`
  // isn't known yet (no local cube resolved), same conservative default
  // main.ts's cubes.onAdd uses.
  function petXrayTint(creature: Creature, me: CreatureRenderMe | null): THREE.Color {
    const self = me ?? { teamId: -1, sessionId: "" };
    const isOwn = me !== null && creature.ownerId === me.sessionId;
    return tintFor(self, { teamId: creature.teamId, sessionId: creature.ownerId }, isOwn);
  }

  function spawnMesh(creature: Creature, me: CreatureRenderMe | null): void {
    // Guard against the ghost-creature state from the schema decoder's
    // skipCurrentStructure recovery path. `undefined` here is transient by
    // design — if the schema heals on a later snapshot, the next reconcile
    // pass spawns normally; in the meantime we don't spam-log every frame.
    if (!creature.creatureType) return;
    const isPet = creature.ownerId !== "";
    const key = creature.creatureType as CreatureType;
    const gltf = assets.creatures[key];
    if (!gltf && ORB_RENDER_TYPES.has(creature.creatureType)) {
      // Orb-backed creature — no glTF, rendered as a persistent pulsing sphere.
      // No SkinnedMesh here, so no x-ray silhouette is possible (v1 gap: a
      // Nightwisp pet won't x-ray through occluders — noted, not blocking).
      const orb = createChannelOrb({ color: NIGHTWISP_ORB_COLOR });
      const group = new THREE.Group();
      group.add(orb.object);
      group.position.set(creature.x, sampleHeight(creature.x, creature.z), creature.z);
      scene.add(group);
      const fakeMixer = new THREE.AnimationMixer(group);
      const adapter: CreatureMesh = {
        object: group,
        mixer: fakeMixer,
        setRotation: () => { /* radially symmetric */ },
        setAction: () => { /* no skeletal actions */ },
        dispose: () => { orb.dispose(); fakeMixer.uncacheRoot(group); scene.remove(group); },
      };
      orbTicks.set(creature.id, (dtMs) => orb.tick(dtMs));
      meshes.set(creature.id, {
        mesh: adapter,
        lastAiState: creature.aiState,
        targetX: creature.x,
        targetZ: creature.z,
        xray: null,
      });
      return;
    }
    if (!gltf) {
      if (!warnedMissingTypes.has(creature.creatureType)) {
        warnedMissingTypes.add(creature.creatureType);
        console.warn(`[creatures] no mesh for type=${creature.creatureType}; skipping`);
      }
      return;
    }
    const mesh = createCreatureMesh(gltf, creature.creatureType);
    mesh.object.position.set(creature.x, sampleHeight(creature.x, creature.z), creature.z);
    mesh.setRotation(creature.facing);
    scene.add(mesh.object);
    // Pets only — wild creatures deliberately excluded (keeps x-ray noise down;
    // easy to extend later by dropping the `isPet` gate if that changes).
    const xray = isPet ? addXraySilhouette(mesh.object, petXrayTint(creature, me)) : null;
    meshes.set(creature.id, {
      mesh,
      lastAiState: creature.aiState,
      targetX: creature.x,
      targetZ: creature.z,
      xray,
    });
  }

  function despawnMesh(id: string): void {
    bufs.delete(id);
    lastRec.delete(id);
    const entry = meshes.get(id);
    if (!entry) return;
    entry.mesh.dispose();
    entry.xray?.dispose();
    meshes.delete(id);
    orbTicks.delete(id);
  }

  function pickCreature(raycaster: THREE.Raycaster): string | null {
    let bestId: string | null = null;
    let bestDist = Infinity;
    // Precise pass: real mesh intersection wins whenever the ray actually hits a
    // creature model (closest along the ray, so a near creature beats a far one).
    meshes.forEach((entry, id) => {
      const hits = raycaster.intersectObject(entry.mesh.object, true);
      if (hits.length === 0) return;
      const d = hits[0]!.distance;
      if (d < bestDist) {
        bestDist = d;
        bestId = id;
      }
    });
    if (bestId) return bestId;
    // Forgiving fallback: no mesh was hit, so accept the nearest-along-the-ray
    // creature whose body axis sits within CREATURE_PICK_RADIUS of the click ray.
    // This makes small creatures (coyotes, night wolves) far easier to target.
    let bestAlong = Infinity;
    meshes.forEach((entry, id) => {
      const p = entry.mesh.object.position;
      _pickPt.set(p.x, p.y + 0.6, p.z); // aim at body center, not the feet
      if (raycaster.ray.distanceToPoint(_pickPt) > CREATURE_PICK_RADIUS) return;
      const along = raycaster.ray.origin.distanceToSquared(_pickPt);
      if (along < bestAlong) {
        bestAlong = along;
        bestId = id;
      }
    });
    return bestId;
  }

  return {
    reconcile(creatureMap, meArg) {
      const me = meArg ?? null;
      const seen = new Set<string>();
      for (const [id, c] of creatureMap) {
        seen.add(id);
        const entry = meshes.get(id);
        if (!entry) {
          spawnMesh(c, me);
        } else {
          // Latest raw server position — only the no-snapshot fallback; the
          // real motion samples the interp buffer fed by record() at patch decode.
          entry.targetX = c.x;
          entry.targetZ = c.z;
          entry.mesh.setRotation(c.facing);
          if (c.aiState !== entry.lastAiState) {
            entry.mesh.setAction(c.aiState as CreatureAction);
            entry.lastAiState = c.aiState;
          }
          // Mid-game taming: the server flips `ownerId` on the SAME creature
          // id in place (tameAnimal) — it never respawns the entry, so
          // spawnMesh's isPet gate never re-runs. Create the silhouette here
          // the first update tick it's owned. (Pets never go back to wild —
          // disconnect deletes the creature from state entirely, see
          // HelloRoom.ts orphanSummons/pet-sweep — so there's no "revert to
          // ''" case to handle; a released/orphaned pet is just despawned.)
          if (c.ownerId !== "") {
            if (entry.xray) {
              // Re-tint on the rare frame a pet spawned before `me` was known
              // (first-to-join edge case, mirrors main.ts cubes.onAdd) —
              // cheap color copy, no-op once the tint has already converged.
              entry.xray.setTint(petXrayTint(c, me));
            } else {
              entry.xray = addXraySilhouette(entry.mesh.object, petXrayTint(c, me));
            }
          }
        }
      }
      // Remove any local mesh whose creature is no longer in the schema
      for (const id of meshes.keys()) {
        if (!seen.has(id)) despawnMesh(id);
      }
    },
    record(id, x, z, now) {
      const prev = lastRec.get(id);
      if (prev !== undefined && Math.hypot(x - prev.x, z - prev.z) > TELEPORT_DIST) {
        // Mid-motion leash/respawn jump: drop history so sample() snaps to the
        // new position instead of streaking an 8u+ lerp across one bracket.
        bufs.delete(id);
      }
      let buf = bufs.get(id);
      if (!buf) { buf = new SnapshotBuffer(); bufs.set(id, buf); }
      buf.record(x, z, now);
      lastRec.set(id, { x, z });
    },
    update(dtSec, renderT) {
      const dtMs = dtSec * 1000;
      meshes.forEach((entry, id) => {
        const pos = entry.mesh.object.position;
        const s = renderT !== undefined ? bufs.get(id)?.sample(renderT) : undefined;
        if (s) {
          pos.x = s.x;
          pos.z = s.z;
        } else {
          // No snapshot yet (spawn frame / record() not wired) — raw snap.
          pos.x = entry.targetX;
          pos.z = entry.targetZ;
        }
        // Sample y per-frame so creatures hug terrain as they glide.
        pos.y = sampleHeight(pos.x, pos.z);
        // For orb-backed creatures, drive the pulse; for glTF creatures, advance
        // the skeletal animation mixer.
        const orbTick = orbTicks.get(id);
        if (orbTick) {
          orbTick(dtMs);
        } else {
          entry.mesh.mixer.update(dtSec);
        }
      });
    },
    pickCreature,
    getObject(id) {
      return meshes.get(id)?.mesh.object ?? null;
    },
    inspect() {
      const out: ReturnType<CreatureRenderManager["inspect"]> = [];
      meshes.forEach((entry, id) => {
        const root = entry.mesh.object;
        let meshCount = 0;
        let hasTextureMap = false;
        let pureWhite = false;
        const materialTypes = new Set<string>();
        // Skip the inverted-hull outline (its material is intentionally a flat
        // black BackSide MeshBasicMaterial added by addCharacterOutline).
        let boneSample: number[] | null = null;
        root.traverse((node) => {
          const anyNode = node as THREE.Mesh & { isSkinnedMesh?: boolean; skeleton?: THREE.Skeleton };
          if (anyNode.isSkinnedMesh && anyNode.skeleton && !boneSample) {
            const b = anyNode.skeleton.bones[Math.min(2, anyNode.skeleton.bones.length - 1)];
            if (b) boneSample = [b.position.x, b.position.y, b.position.z, b.quaternion.x, b.quaternion.y, b.quaternion.z, b.quaternion.w];
          }
          if (!anyNode.isMesh) return;
          // Outline hull + x-ray silhouette are both extra render passes over
          // the same geometry, not the creature's own material — exclude both
          // from the "is the model textured / white" verdict by name suffix.
          if (typeof anyNode.name === "string" && anyNode.name.endsWith(":xray")) return;
          const mats = Array.isArray(anyNode.material) ? anyNode.material : [anyNode.material];
          for (const m of mats) {
            if (!m) continue;
            // outline hull = MeshBasicMaterial w/ side BackSide; exclude from
            // the "is the model textured / white" verdict.
            const isOutline = (m as THREE.Material).side === THREE.BackSide && (m as any).color?.getHex?.() === 0x000000;
            if (isOutline) continue;
            meshCount++;
            materialTypes.add(m.type);
            const map = (m as any).map;
            if (map) hasTextureMap = true;
            const col = (m as any).color as THREE.Color | undefined;
            const isWhite = col ? col.getHex() === 0xffffff : false;
            if (!map && isWhite) pureWhite = true;
          }
        });
        const mixer = entry.mesh.mixer as THREE.AnimationMixer & { _actions?: any[] };
        const actions: any[] = (mixer as any)._actions ?? [];
        const activeActions = actions.filter((a) => a && a.isRunning && a.isRunning()).length;
        out.push({
          id,
          meshCount,
          materialTypes: [...materialTypes],
          hasTextureMap,
          pureWhite,
          mixerRunning: mixer.time > 0,
          activeActions,
          boneSample,
          renderPos: [root.position.x, root.position.z],
        });
      });
      return out;
    },
    dispose() {
      meshes.forEach((entry) => {
        entry.mesh.dispose();
        entry.xray?.dispose();
      });
      meshes.clear();
      orbTicks.clear();
    },
  };
}
